import { Clock, Trophy } from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';

interface UptimeSection {
  avgUptimeDays: number;
  longestUptimeDays: number;
  longestUptimeNodeName: string | null;
  shortestUptimeDays: number;
  shortestUptimeNodeName: string | null;
  shortestUptimeInsight?: string | null;
}

interface UptimeStatsProps {
  networkUptimeStats: UptimeSection;
  uptimeStats: UptimeSection;
}

function formatDays(days: number) {
  if (days >= 1) return `${Math.floor(days)} days`;
  const hours = days * 24;
  if (hours >= 1) return `${Math.floor(hours)} hours`;
  return `${Math.floor(hours * 60)} min`;
}

function TextBlock({
  label,
  value,
  nodeName,
  insight,
  large,
}: {
  label: string;
  value: string;
  nodeName?: string | null;
  insight?: string | null;
  large?: boolean;
}) {
  return (
    <div>
      <p className="dls-text-xs dls-text-muted-foreground">{label}</p>
      <p className={large ? 'dls-text-lg dls-font-bold' : 'dls-text-sm dls-font-semibold'}>{value}</p>
      {nodeName && <p className="dls-text-xs dls-text-muted-foreground">{nodeName}</p>}
      {insight && <p className="dls-text-xs dls-text-muted-foreground/70 dls-italic dls-mt-0.5">{insight}</p>}
    </div>
  );
}

export function UptimeStats({ networkUptimeStats, uptimeStats }: UptimeStatsProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
          Uptime
          <InfoTooltip text="Network uptime measures continuous connectivity (time since last state change). Hardware uptime measures time since last reboot. Hover each column title for details." />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* 5-col grid: [net-icon | net-text | spacer | hw-icon | hw-text] */}
        <div className="dls-grid dls-grid-cols-[auto_1fr_1.5rem_auto_1fr] dls-gap-x-2 dls-gap-y-4 dls-items-start">
          {/* Row: Titles */}
          <p className="dls-col-span-2 dls-text-sm dls-font-semibold dls-text-muted-foreground dls-flex dls-items-center dls-gap-1">
            Network
            <InfoTooltip text="Time since each node last transitioned to alive. Measures continuous network connectivity — resets when a node goes offline and comes back, even without a reboot." />
          </p>
          <div />
          <p className="dls-col-span-2 dls-text-sm dls-font-semibold dls-text-muted-foreground dls-flex dls-items-center dls-gap-1">
            Hardware
            <InfoTooltip text="Time since each node's last reboot, measured from boot time. Only resets when the hardware or VM actually restarts, regardless of network connectivity." />
          </p>

          {/* Row: Longest Uptime */}
          <Trophy className="dls-h-4 dls-w-4 dls-text-status-warning dls-mt-0.5" />
          <TextBlock
            label="Longest Uptime"
            value={formatDays(networkUptimeStats.longestUptimeDays)}
            nodeName={networkUptimeStats.longestUptimeNodeName}
          />
          <div />
          <Trophy className="dls-h-4 dls-w-4 dls-text-status-warning dls-mt-0.5" />
          <TextBlock
            label="Longest Uptime"
            value={formatDays(uptimeStats.longestUptimeDays)}
            nodeName={uptimeStats.longestUptimeNodeName}
          />

          {/* Row: Average Uptime */}
          <Clock className="dls-h-4 dls-w-4 dls-text-muted-foreground dls-mt-1" />
          <TextBlock
            label="Average Uptime"
            value={formatDays(networkUptimeStats.avgUptimeDays)}
            large
          />
          <div />
          <Clock className="dls-h-4 dls-w-4 dls-text-muted-foreground dls-mt-1" />
          <TextBlock label="Average Uptime" value={formatDays(uptimeStats.avgUptimeDays)} large />

          {/* Row: Shortest Uptime */}
          <Clock className="dls-h-4 dls-w-4 dls-text-status-info dls-mt-0.5" />
          <TextBlock
            label="Shortest Uptime"
            value={formatDays(networkUptimeStats.shortestUptimeDays)}
            nodeName={networkUptimeStats.shortestUptimeNodeName}
          />
          <div />
          <Clock className="dls-h-4 dls-w-4 dls-text-status-info dls-mt-0.5" />
          <TextBlock
            label="Shortest Uptime"
            value={formatDays(uptimeStats.shortestUptimeDays)}
            nodeName={uptimeStats.shortestUptimeNodeName}
            insight={uptimeStats.shortestUptimeInsight}
          />
        </div>
      </CardContent>
    </Card>
  );
}
